// Class sections CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import { getSchoolContext } from './school-context'
import type {
  ClassSectionWithRelations,
  CreateClassSectionRequest,
  UpdateClassSectionRequest,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}


// Get all class sections
export async function getClassSectionsDirect(schoolId?: string): Promise<ClassSectionWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Apply school context filter (unless Super Admin without context)
    const schoolContext = getSchoolContext()
    const effectiveSchoolId = schoolId || schoolContext

    const params = new URLSearchParams()
    
    if (effectiveSchoolId) {
      // Use !inner to do an inner join and filter on the related table
      params.append('select', '*,class:classes!inner(*,class_schedule:class_schedules(id,name,working_days)),class_teacher:teachers(*)')
      params.append('class.school_id', `eq.${effectiveSchoolId}`)
    } else {
      params.append('select', '*,class:classes(*,class_schedule:class_schedules(id,name,working_days)),class_teacher:teachers(*)')
    }
    params.append('order', 'created_at.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch class sections')
    }

    return await response.json()
  })
}

// Get class sections by class ID
export async function getClassSectionsByClassDirect(classId: string): Promise<ClassSectionWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,class:classes(*,class_schedule:class_schedules(id,name,working_days)),class_teacher:teachers(*)')
    params.append('class_id', `eq.${classId}`)
    params.append('order', 'section.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch class sections')
    }

    return await response.json()
  })
}

// Get a single class section by ID
export async function getClassSectionByIdDirect(id: string): Promise<ClassSectionWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,class:classes(*,class_schedule:class_schedules(id,name,working_days)),class_teacher:teachers(*)')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Class section not found')
    }

    return await response.json()
  })
}

// Check if a teacher is already assigned as class teacher in another section
async function checkClassTeacherConflict(
  teacherId: string,
  excludeSectionId?: string
): Promise<void> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const params = new URLSearchParams()
  params.append('select', '*,class:classes(class_name),class_teacher:teachers(name)')
  params.append('class_teacher_id', `eq.${teacherId}`)

  const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?${params}`, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) return // don't block on check failure

  const existing: any[] = await response.json()
  const conflict = existing.find((s) => s.id !== excludeSectionId)
  if (conflict) {
    const className = conflict.class?.class_name || 'another class'
    const sectionName = conflict.section || ''
    const teacherName = conflict.class_teacher?.name || 'This teacher'
    throw new Error(
      `${teacherName} is already the class teacher of ${className}-${sectionName}. A teacher can only be assigned to one section.`
    )
  }
}

// Create a new class section
export async function createClassSectionDirect(
  section: CreateClassSectionRequest
): Promise<ClassSectionWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Validate class teacher uniqueness before creating
    if (section.class_teacher_id) {
      await checkClassTeacherConflict(section.class_teacher_id)
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(section),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create class section')
    }

    const data: any[] = await response.json()
    const created = data[0]

    // Fetch with relations
    return getClassSectionByIdDirect(created.id)
  })
}

// Update a class section
export async function updateClassSectionDirect(
  id: string,
  updates: UpdateClassSectionRequest
): Promise<ClassSectionWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Validate class teacher uniqueness before updating (exclude current section)
    if (updates.class_teacher_id) {
      await checkClassTeacherConflict(updates.class_teacher_id, id)
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update class section')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Class section not found')

    // Fetch with relations
    return getClassSectionByIdDirect(id)
  })
}

// Delete a class section
export async function deleteClassSectionDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_sections?id=eq.${id}`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete class section')
    }
  })
}
